import { FC, useState, useEffect, useCallback } from "react"
import { Paper } from "@mui/material"
import {
  DataGridPremium,
  useGridApiRef,
  GridPaginationModel,
  gridFilteredTopLevelRowCountSelector,
  gridFilterModelSelector,
  GridPinnedColumns,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
} from '@mui/x-data-grid-premium'
import { DataLogger } from "src/api"
import { TableToolbar } from "src/components"
import { SearchParamsKeys } from "src/enums"
import { defaultRowsValuePerPage, defaultPage, defaultPerPage } from "src/config"
import { healthCellPalette } from "src/helpers"
import { DataLoggerGridColumns } from "./dataLoggerColumns"

interface DataLoggerListProps {
  dataLoggers: DataLogger[]
  dataLoggerCount: number
  isLoading: boolean
  queryParams: Record<string, string>
  setQueryParams: (params: Record<string, string>, paramsToRemove?: string[]) => void
  autoHeight?: boolean
  tableColumns?: GridColDef<DataLogger>[]
  exportedFileName?: string
}

const defaultColumns: GridColDef<DataLogger>[] = [
  DataLoggerGridColumns.DeviceId,
  DataLoggerGridColumns.Status,
  DataLoggerGridColumns.Label,
  DataLoggerGridColumns.Interval,
  DataLoggerGridColumns.MatchReadingsToTimeInterval,
  DataLoggerGridColumns.FirstReadingDateTime,
  DataLoggerGridColumns.LastReadingDateTime,
  DataLoggerGridColumns.LastUploadDateTime,
  DataLoggerGridColumns.Contractor,
  DataLoggerGridColumns.Project,
  DataLoggerGridColumns.Section,
  DataLoggerGridColumns.BatteryLevel,
  DataLoggerGridColumns.CreatedAt,
  DataLoggerGridColumns.DaysSinceActivation,
]
const defaultRowsPerPageOptions = defaultRowsValuePerPage

const healthCellStyles: Record<string, { backgroundColor: string; color: string }> = Object.fromEntries(
  Object.entries(healthCellPalette).map(([severity, colors]) => [`& .MuiDataGrid-cell--health-${severity}`, { backgroundColor: colors.background, color: colors.text }])
)

export const DataLoggerList: FC<DataLoggerListProps> = ({
  dataLoggerCount,
  dataLoggers,
  isLoading,
  setQueryParams,
  queryParams,
  autoHeight = false,
  tableColumns,
  exportedFileName,
}) => {
  const apiRef = useGridApiRef()
  const columns = tableColumns && tableColumns.length > 0 ? tableColumns : defaultColumns
  const [rowCountState, setRowCountState] = useState(dataLoggerCount)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "probeId"],
  })

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      dataLoggerCount !== undefined ? dataLoggerCount : prevRowCountState
    )
  }, [dataLoggerCount, setRowCountState])

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns)
    },
    []
  )

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const newPage = pagination.page + 1
    setQueryParams({
      [SearchParamsKeys.Page]: `${newPage}`,
      [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`,
    }, [
      SearchParamsKeys.Page,
      SearchParamsKeys.RowsPerPage,
    ])
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    }
    else setRowCountState(dataLoggerCount)
  }

  const page = queryParams["page"] ? parseInt(queryParams["page"], 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams["rowsPerPage"], 10) || defaultPerPage

  return (
    <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%' }}>
      <DataGridPremium<DataLogger>
        apiRef={apiRef}
        pagination
        autoHeight={autoHeight}
        pinnedColumns={pinnedColumns}
        disableRowSelectionOnClick
        rows={dataLoggers}
        columns={columns}
        rowCount={rowCountState}
        loading={isLoading}
        paginationModel={{ page, pageSize }}
        rowThreshold={2}
        columnThreshold={2}
        paginationMode="server"
        pageSizeOptions={defaultRowsPerPageOptions}
        onPaginationModelChange={handleChangePaginationModel}
        onStateChange={handleChangeState}
        localeText={{
          columnMenuSortAsc: "Sort A-Z",
          columnMenuSortDesc: "Sort Z-A",
        }}
        slots={{
          toolbar: () => <TableToolbar exportedFileName={exportedFileName} />
        }}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        sx={(theme) => ({
          border: "none",
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            paddingLeft: "5px",
            overflow: "visible",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: 1,
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(0, 0, 0, 0.6)",
            overflow: "visible",
          },
          ...healthCellStyles,
        })}
      />
    </Paper>
  )
}
